// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Decision Tree Computation API Service
 * 
 * Purpose: API client for R backend decision tree computation
 * Endpoint: POST /api/decision-tree/compute
 * 
 * @note Follows existing API patterns from src/services/apiService.ts
 * @note CSRF token handling matches existing implementation
 */

import { 
  ModelMetadata,
  CostParameters,
  PopulationParameters,
  SimulationParameters
} from '../lib/validation';
import type { TreeStateForAPI } from '../store/vaccineClientStore';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * EasyHTA parameters combining all parameter groups
 */
export interface EasyHTAParameters {
  model_metadata: ModelMetadata;
  cost_parameters: CostParameters;
  population_parameters: PopulationParameters;
  simulation_parameters: SimulationParameters;
}

/**
 * Request format matching R backend expectations
 * @see backend/server/decision_tree_handlers.R for server-side validation
 */
export interface DecisionTreeRequest {
  tree: TreeStateForAPI; // API expects array format
  parameters: {
    model_metadata: any;
    cost_parameters: any;
    population_parameters: any;
    simulation_parameters: any;
  };
}

/**
 * Response format from R backend computation
 * 
 * @note This interface uses raw types (RawIncrementalResults, RawMetrics) to represent
 * what the backend currently returns. The mapper converts these to clean domain types
 * (IncrementalResults, Metrics) for use throughout the application.
 * 
 * @see R/decision_tree_service.R for computation logic
 * @see mapBackendResponseToVaccineOutputs() for raw → domain conversion
 */
export interface DecisionTreeResponse {
  results: {
    vaccinate: StrategyResults;
    no_vaccinate: StrategyResults;
    series: BackendSeriesData[];
    regional_breakdown: BackendRegionalMetric[];
    waterfall_data: BackendWaterfallData;
  };
  incremental: RawIncrementalResults;
  metrics: RawMetrics;
  elapsed_ms: number;
  request_id: string;
  // Multi-year analysis output
  veByYear?: number[]; // VE values for years 1..H (camelCase from backend)
}

export interface BackendSeriesData {
  year: number;
  programme_cost: number;
  healthcare_savings: number;
  net_impact: number;
  cumulative_net_impact: number;
  // Multi-year analysis fields (camelCase from backend - see R/domain/decision_tree/results/time_series.R)
  vaccineEfficacy?: number; // VE value for this year
  qalysWithVaccine?: number; // Undiscounted QALYs for vaccinated group
  qalysWithoutVaccine?: number; // Undiscounted QALYs for unvaccinated group
  qalysWithVaccineDiscounted?: number; // qalysWithVaccine * discountFactor (per-cycle); sums to headline cumulative discounted QALYs
  qalysWithoutVaccineDiscounted?: number;
  discountFactor?: number; // Per-cycle start-of-year factor 1/(1+r)^(year-1); 1 when discounting disabled or H==1
  baselineCases?: number; // Infections in unvaccinated group
  withVaccineCases?: number; // Infections in vaccinated group
  aliveNovac?: number; // Cohort alive at start of cycle, no-vaccine arm
  aliveVac?: number; // Cohort alive at start of cycle, vaccinate arm
  backgroundMortality?: number; // Per-cycle background all-cause mortality (p_death_bg), shared across arms
  backgroundUtility?: number; // Per-cycle general-population HRQoL multiplier (bg_util), shared across arms; null/undefined for Y1 when Y1 QALYs come from the decision tree
  healthcare_cost_novac?: number; // Healthcare costs without vaccine (accounts for mortality)
  healthcare_cost_vac?: number; // Healthcare costs with vaccine (accounts for mortality)
  bgDeathsNovac?: number; // Per-cycle background deaths in no-vaccine arm (independent-risks attribution)
  bgDeathsVac?: number; // Per-cycle background deaths in vaccinate arm (independent-risks attribution)
  deathsNovac?: number; // Per-cycle total deaths in no-vaccine arm (competing risks: bg + disease)
  deathsVac?: number; // Per-cycle total deaths in vaccinate arm (competing risks: bg + disease)
}

export interface BackendRegionalMetric {
  id: string;
  name: string;
  population: number;
  coverage: number;
  cases_prevented: number;
  hospitalizations_prevented: number;
  life_years_gained: number;
  cost_impact: number;
}

export interface BackendWaterfallData {
  treatment_savings: number;
  productivity_savings: number;
  adverse_event_cost: number;
  programme_cost: number;
}

export interface StrategyResults {
  qalys: number;
  life_years: number;
  deaths: number;
  cases: number;
  hospitalizations: number;
  total_cost: number;
  programme_cost: number;
  healthcare_cost: number;
  indirect_cost: number;
  events: number;
}

/**
 * Raw incremental results from backend (transitional type)
 * 
 * @note The R backend may return arrays for some fields due to technical artifacts.
 * Arrays are only meaningful if they represent time series or subgroups.
 * Otherwise, they should be treated as data quality issues and fixed at source.
 * 
 * @see IncrementalResults for the clean domain model (always scalar)
 */
export interface RawIncrementalResults {
  /**
   * Incremental QALYs gained (vaccinate - no vaccinate)
   * @note Backend should return scalar for single scenario/cohort.
   * Array indicates either time series (should be separate field) or R artifact (should be fixed).
   */
  delta_qalys: number | number[];
  delta_life_years: number | number[];
  delta_cost: number | number[];
  deaths_averted: number | number[];
  cases_avoided: number | number[];
  hospitalizations_avoided: number | number[];
  events_prevented: number | number[];
}

/**
 * Clean domain model for incremental results (always scalar)
 * 
 * This is the type used throughout the application after raw backend data
 * has been converted through the mapper.
 */
export interface IncrementalResults {
  delta_qalys: number;
  delta_life_years: number;
  delta_cost: number;
  deaths_averted: number;
  cases_avoided: number;
  hospitalizations_avoided: number;
  events_prevented: number;
}

/**
 * Raw metrics from backend (transitional type)
 * 
 * @note The R backend may return arrays for some fields due to technical artifacts.
 * Arrays are only meaningful if they represent time series or subgroups.
 * Otherwise, they should be treated as data quality issues and fixed at source.
 * 
 * @see Metrics for the clean domain model (always scalar)
 */
export interface RawMetrics {
  icer: number | number[];
  bcr: number | number[];
  /**
   * Budget impact (net budget increase/decrease from vaccine programme)
   * @note Backend should return scalar for single scenario/cohort.
   * Array indicates either time series (should be separate field like `budget_impact_by_year`)
   * or R artifact (should be fixed).
   */
  budget_impact: number | number[];
}

/**
 * Clean domain model for metrics (always scalar)
 * 
 * This is the type used throughout the application after raw backend data
 * has been converted through the mapper.
 */
export interface Metrics {
  icer: number;
  bcr: number;
  budget_impact: number;
}

/**
 * Error response from R backend
 * @see backend/server/decision_tree_handlers.R for error codes
 */
export interface ComputationError {
  code: string; // EHTA-DT-XXX format
  message: string;
  request_id: string;
  details?: string[];
}

/**
 * API error with structured details
 */
export class DecisionTreeApiError extends Error {
  public readonly code: string;
  public readonly requestId: string;
  public readonly status: number;
  public readonly details?: string[];

  constructor(error: ComputationError, status: number) {
    super(error.message);
    this.name = 'DecisionTreeApiError';
    this.code = error.code;
    this.requestId = error.request_id;
    this.status = status;
    this.details = error.details;
  }
}

// ============================================================================
// CSRF Token Management (matches existing pattern)
// ============================================================================

let csrfToken: string | null = null;

/**
 * Ensure CSRF token is available for authenticated requests
 * @note Pattern copied from src/services/apiService.ts
 */
async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;

  try {
    const res = await fetch('/api/auth/csrf', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    });

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await res.text();
      const preview = text.slice(0, 120);
      console.warn('CSRF endpoint returned non-JSON. Content-Type:', contentType || 'none', 'First 120 chars:', preview);
      throw new Error(`CSRF endpoint misrouted (content-type: ${contentType || 'none'})`);
    }

    if (!res.ok) {
      console.warn(`CSRF endpoint not available (${res.status}), proceeding without CSRF protection`);
      return 'no-csrf';
    }

    const data = await res.json();
    const { csrf_token } = data;
    csrfToken = csrf_token;
    return csrfToken!;
  } catch (error) {
    if (error instanceof Error && error.message.includes('misrouted')) {
      console.error('CSRF endpoint misrouted - likely NGINX/CloudFront routing issue:', error.message);
    } else {
      console.warn('CSRF endpoint not available, proceeding without CSRF protection:', error);
    }
    return 'no-csrf';
  }
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Compute decision tree results via R backend
 * 
 * @param request - Decision tree state and parameters
 * @param signal - AbortSignal for request cancellation
 * @returns Computation results with QALYs, costs, ICER, BCR, budget impact
 * @throws DecisionTreeApiError with structured error details
 * 
 * @example
 * ```typescript
 * const results = await computeDecisionTree({
 *   tree: vaccineClientStore.getState(),
 *   parameters: vaccineClientStore.getState().parameters
 * });
 * console.log('ICER:', results.metrics.icer);
 * ```
 */
export async function computeDecisionTree(
  request: DecisionTreeRequest,
  signal?: AbortSignal
): Promise<DecisionTreeResponse> {
  try {
    // Get CSRF token
    const csrf = await ensureCsrf();

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Request-Id': crypto.randomUUID?.() ?? String(Date.now()),
    };

    // Only include CSRF token if we have a real one
    if (csrf && csrf !== 'no-csrf') {
      headers['X-CSRF-Token'] = csrf;
    }

    // Make API request
    const response = await fetch('/api/decision-tree/compute', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(request),
      signal: signal || AbortSignal.timeout(30000), // 30s timeout (backend target: P95 ≤ 5s)
    });

    // Check Content-Type
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      // Try to parse error response
      let errorData: ComputationError | null = null;

      if (contentType.includes('application/json')) {
        try {
          const errorResponse = await response.json();
          
          // Handle structured error from our R service
          if (errorResponse.error && typeof errorResponse.error === 'object') {
            errorData = errorResponse.error;
          } 
          // Handle plumber default error format {"error": "message"}
          else if (errorResponse.error && typeof errorResponse.error === 'string') {
            errorData = {
              code: 'EHTA-DT-500',
              message: errorResponse.error,
              request_id: 'unknown'
            };
          }
        } catch {
          // Failed to parse JSON error
        }
      }

      if (errorData) {
        throw new DecisionTreeApiError(errorData, response.status);
      }

      // Fallback error
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse success response
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      const preview = text.slice(0, 120);
      console.warn('Decision tree endpoint returned non-JSON. Content-Type:', contentType || 'none', 'First 120 chars:', preview);
      throw new Error(`Unexpected response format (content-type: ${contentType || 'none'})`);
    }

    const data: DecisionTreeResponse = await response.json();

    // Log performance
    if (data.elapsed_ms > 5000) {
      console.warn(`Decision tree computation exceeded P95 target: ${data.elapsed_ms}ms (request_id: ${data.request_id})`);
    }

    return data;

  } catch (error) {
    // Re-throw DecisionTreeApiError as-is
    if (error instanceof DecisionTreeApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to reach decision tree API');
    }

    // Handle abort
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Request cancelled');
    }

    // Handle timeout
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new Error('Request timeout: Decision tree computation took too long');
    }

    // Re-throw other errors
    throw error;
  }
}

// ============================================================================
// Future Enhancement: TanStack Query Integration
// ============================================================================
//
// When TanStack Query is added to the project:
//
// 1. Install dependency:
//    pnpm add @tanstack/react-query
//
// 2. Create QueryClient provider in vaccine-client entry point:
//    ```typescript
//    import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
//    
//    const queryClient = new QueryClient({
//      defaultOptions: {
//        queries: {
//          staleTime: 5 * 60 * 1000, // 5 minutes
//          retry: 1,
//        },
//      },
//    });
//    
//    <QueryClientProvider client={queryClient}>
//      <VaccineClientApp />
//    </QueryClientProvider>
//    ```
//
// 3. Create useComputeDecisionTree hook:
//    ```typescript
//    import { useMutation } from '@tanstack/react-query';
//    
//    export function useComputeDecisionTree() {
//      return useMutation({
//        mutationFn: computeDecisionTree,
//        onSuccess: (data) => {
//          console.log('Computation complete:', data.request_id);
//        },
//        onError: (error: Error) => {
//          console.error('Computation failed:', error.message);
//        },
//      });
//    }
//    ```
//
// 4. Use in components:
//    ```typescript
//    const { mutate, isPending, error, data } = useComputeDecisionTree();
//    
//    const handleCompute = () => {
//      mutate({
//        tree: vaccineClientStore.getState(),
//        parameters: vaccineClientStore.getState().parameters,
//      });
//    };
//    ```
//
// Benefits:
// - Automatic loading/error/success states
// - Built-in retry logic
// - Request deduplication
// - Cache management
// - Optimistic updates
//
// ============================================================================
